import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useLazyQuery } from '@apollo/client';
import get from 'lodash/get';
import Fuse from 'fuse.js/dist/fuse.min.js'; // this weird import makes it work in Edge/IE11
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { ActionLink } from 'components/shared';

import SavedSnippets from './SavedSnippets';
import SnippetEditor from './SnippetEditor';
import { TEXT_SNIPPET_QUERY } from './gql/snippets.graphql.js';
import { Spinner } from '@companycam/slab-web';
import { translate } from 'translations/i18n';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: sticky;
  top: 0;
  background-color: white;
  padding: 40px 0 0 48px;
  z-index: 1000;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 50% 50%;
  grid-template-rows: 100%;
`;

const options = {
  threshold: 0.3,
  ignoreLocation: true,
  keys: ['content', 'title'],
};

const SnippetsLibraryModal = ({
  isOpen,
  onRequestClose,
  selectedText,
  insertSnippet,
  setSelectedText,
}) => {
  const [
    getTextSnippets,
    { loading: getLoading, data: snippetsData },
  ] = useLazyQuery(TEXT_SNIPPET_QUERY, { fetchPolicy: 'network-only' });

  const [filteredSnippets, setFilteredSnippets] = useState([]);
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const [searchQuery, setSearchQuery] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fuse = new Fuse(filteredSnippets, options);

  useEffect(
    () => {
      if (isOpen) {
        getTextSnippets();
      }
    },
    [isOpen, getTextSnippets],
  );

  // Set snippets and tags on snippets query
  useEffect(
    () => {
      if (snippetsData) {
        const snippets = get(
          snippetsData,
          'viewer.company.reportsTextSnippet.nodes',
          [],
        );
        setFilteredSnippets(snippets);
      }
    },
    [snippetsData],
  );

  const filterSearchSnippets = () => {
    if (searchQuery) {
      const res = fuse.search(searchQuery);
      return res.map((result) => result.item);
    } else {
      return filteredSnippets;
    }
  };

  const handleSnippetClick = (snippet) => {
    setIsCreating(false);
    setIsDeleting(false);
    setSelectedSnippet(snippet);
    setSelectedText(null);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsDeleting(false);
    setSelectedSnippet(null);
    setSelectedText(null);
  };

  const handleInsertSnippetClick = (e, snippetText) => {
    if (e) {
      e.stopPropagation();
    }
    setSearchQuery('');
    handleCancel();
    insertSnippet(snippetText);
  };
  const handleCreateClick = () => {
    setIsCreating(true);
    setIsDeleting(false);
    setSelectedSnippet(null);
  };

  const handleDeleteClick = () => {
    setIsDeleting(true);
  };

  const handleDeleteCancel = (deleted) => {
    setIsDeleting(false);

    if (deleted) {
      setSelectedSnippet(null);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    handleCancel();
    onRequestClose();
  };

  useEffect(
    () => {
      if (selectedText) {
        handleCreateClick();
      }
    },
    [selectedText],
  );
  return (
    <ReactModal
      isOpen={isOpen}
      contentLabel="Text Snippets"
      overlayClassName="modal"
      className="modal-content-wrap is-fullscreen modal-scroll manage-snippets"
    >
      <React.Fragment>
        <Header>
          <h3 className="modal-title">
            {translate('snippets.modal.title')}
          </h3>
          <ActionLink className="modal-close-button" onClick={handleClose}>
            <i className="mdi mdi-close" />
          </ActionLink>
        </Header>

        <Body>
          {getLoading ? (
            <Spinner accessibilityLabel={translate('common.loading')} />
          ) : (
            <React.Fragment>
              <SavedSnippets
                snippets={filterSearchSnippets()}
                selectedSnippet={selectedSnippet}
                isCreating={isCreating}
                isDeleting={isDeleting}
                handleSearchChange={(e) => setSearchQuery(e.target.value)}
                handleSnippetClick={handleSnippetClick}
                handleUseSnippetClick={handleInsertSnippetClick}
                handleCreateClick={handleCreateClick}
              />
              <SnippetEditor
                snippets={filterSearchSnippets()}
                selectedText={selectedText}
                selectedSnippet={selectedSnippet}
                isCreating={isCreating}
                isDeleting={isDeleting}
                insertSnippet={handleInsertSnippetClick}
                handleSelectSnippet={handleSnippetClick}
                handleSelectDelete={handleDeleteClick}
                handleCancel={handleCancel}
                handleDeleteCancel={handleDeleteCancel}
              />
            </React.Fragment>
          )}
        </Body>
      </React.Fragment>
    </ReactModal>
  );
};

SnippetsLibraryModal.propTypes = {
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  selectedText: PropTypes.string,
  insertSnippet: PropTypes.func,
  setSelectedText: PropTypes.func,
};

export default SnippetsLibraryModal;
